import React, { useEffect, useState } from "react";
import { bigNumberify, formatAmount, formatAmountFree, parseValue } from "../../../lib/numbers";
import { ethers } from "ethers";
import Vester from "../../../abis/Vester.json";
import { callContract } from "../../../lib/contracts";
import Tooltip from "../../Tooltip/Tooltip";
import StatsTooltipRow from "../../StatsTooltip/StatsTooltipRow";
import Button from "../../Button/Button";
import { t, Trans } from "@lingui/macro";
import { ModalBase } from "../ModalBase/ModalBase";
import classes from "./VesterDepositModalStyles.module.scss";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "../../../lib/chains";
import classNames from "classnames";
import { uniqueId } from "lodash";
import { ActionButton } from "../../Buttons/ActionButton/ActionButton";

type Props = {
  title: string;
  stakeTokenLabel: string;
  maxAmount: any;
  balance: any;
  // escrowedBalance: any;
  vestedAmount: any;
  maxVestableAmount: any;
  averageStakedAmount: string;
  reserveAmount: any;
  maxReserveAmount: any;
  vesterAddress: string;
  setPendingTxns: any;
  onClose: () => void;
};

export const VesterDepositModal = ({
  title,
  stakeTokenLabel,
  maxAmount,
  balance,
  // escrowedBalance,
  vestedAmount,
  maxVestableAmount,
  averageStakedAmount,
  reserveAmount,
  maxReserveAmount,
  vesterAddress,
  setPendingTxns,
  onClose,
}: Props) => {
  const { library } = useWeb3React();
  const { chainId } = useChainId();

  const [isDepositing, setIsDepositing] = useState(false);
  const [value, setValue] = useState("");
  const [amount, setAmount] = useState<ethers.BigNumber | undefined>();

  useEffect(() => {
    setAmount(parseValue(value, 18));
  }, [value]);

  // TODO: need to fix complicated mutations

  let nextReserveAmount = reserveAmount === "" ? bigNumberify(0) : reserveAmount;

  let nextDepositAmount = vestedAmount;
  if (amount) {
    nextDepositAmount = amount ? vestedAmount.add(amount) : vestedAmount;
  }

  let additionalReserveAmount = bigNumberify(0);
  if (amount && averageStakedAmount && maxVestableAmount && maxVestableAmount.gt(0)) {
    nextReserveAmount = nextDepositAmount.mul(averageStakedAmount).div(maxVestableAmount);
    if (nextReserveAmount.gt(reserveAmount)) {
      additionalReserveAmount = nextReserveAmount.sub(reserveAmount);
    }
  }

  const getError = () => {
    if (!amount || amount.eq(0)) {
      return t`Enter an amount`;
    }
    if (maxAmount && amount.gt(maxAmount)) {
      return t`Max amount exceeded`;
    }
    if (nextReserveAmount.gt(maxReserveAmount)) {
      return t`Insufficient staked tokens`;
    }
  };

  const onClickPrimary = () => {
    setIsDepositing(true);
    const contract = new ethers.Contract(vesterAddress, Vester.abi, library.getSigner());

    callContract(chainId, contract, "deposit", [amount], {
      sentMsg: t`Deposit submitted!`,
      failMsg: t`Deposit failed!`,
      successMsg: t`Deposited!`,
      setPendingTxns,
    })
      .then(async (res) => {
        onClose();
      })
      .finally(() => {
        setIsDepositing(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isDepositing) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isDepositing) {
      return t`Depositing...`;
    }
    return t`Deposit`;
  };

  const statsData = [
    {
      title: "Wallet",
      content: <>{formatAmount(balance, 18, 2, true)} esCAD</>,
    },
    {
      title: "Vault Capacity",
      content: (
        <Tooltip
          handle={`${formatAmount(nextDepositAmount, 18, 2, true)} / ${formatAmount(maxVestableAmount, 18, 2, true)}`}
          position="right-bottom"
          renderContent={() => (
            <div>
              <p className="text-white">
                <Trans>Vault Capacity for your Account:</Trans>
              </p>
              <StatsTooltipRow
                showDollar={false}
                label={t`Deposited`}
                value={`${formatAmount(vestedAmount, 18, 2, true)} esCAD`}
              />
              <StatsTooltipRow
                showDollar={false}
                label={t`Max Capacity`}
                value={`${formatAmount(maxVestableAmount, 18, 2, true)} esCAD`}
              />
            </div>
          )}
        />
      ),
    },
    {
      title: "Reserve Amount",
      content: (
        <Tooltip
          handle={`${formatAmount(
            reserveAmount && reserveAmount.gte(additionalReserveAmount) ? reserveAmount : additionalReserveAmount,
            18,
            2,
            true
          )} / ${formatAmount(maxReserveAmount, 18, 2, true)}`}
          position="right-bottom"
          renderContent={() => {
            return (
              <>
                <StatsTooltipRow
                  label={t`Current Reserved`}
                  value={formatAmount(reserveAmount, 18, 2, true)}
                  showDollar={false}
                />
                <StatsTooltipRow
                  label={t`Additional reserve required`}
                  value={formatAmount(additionalReserveAmount, 18, 2, true)}
                  showDollar={false}
                />
                {amount && nextReserveAmount.gt(maxReserveAmount) && (
                  <>
                    <br />
                    <Trans>
                      You need a total of at least {formatAmount(nextReserveAmount, 18, 2, true)} {stakeTokenLabel} to
                      vest {formatAmount(amount, 18, 2, true)} esCAD.
                    </Trans>
                  </>
                )}
              </>
            );
          }}
        />
      ),
    },
  ];

  return (
    <div className="StakeModal">
      <ModalBase title={title} onClose={onClose}>
        <div className={classes.wrapper}>
          <div className={classes.inputWrapper}>
            <div className={classes.content}>
              <span className={classes.title}>
                <Trans>Stake</Trans>
              </span>
              <span
                className={classNames(classes.title, classes.titleClickable)}
                onClick={() => setValue(formatAmountFree(maxAmount, 18, 18))}
              >
                <Trans>Max</Trans>: {formatAmount(maxAmount, 18, 4, true)}
              </span>
            </div>
            <div className={classes.content}>
              <input
                type="number"
                placeholder="0.0"
                className={classes.input}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <span className={classes.symbolTitle}>esCad</span>
            </div>
          </div>

          <div className={classes.statsData}>
            {statsData.map((statsItem) => (
              <div key={uniqueId(statsItem.title)} className={classes.itemWrapper}>
                <span className={classes.title}>{statsItem.title}</span>
                <div className={classes.data}>{statsItem.content}</div>
              </div>
            ))}
          </div>

          <ActionButton
            onClick={onClickPrimary}
            type={"outlined"}
            buttonText={getPrimaryText()}
            disabled={!isPrimaryEnabled()}
            className={classes.button}
          />
        </div>
      </ModalBase>
    </div>
  );
};
